/* ============================================================================
   PAPI CART — real Shopify cart, with the mix & match Chunky box on top
   ----------------------------------------------------------------------------
   BOXES ARE EXPLICIT (rewritten 2026-08-01, v4)
   ---------------------------------------------
   The previous version held ONE POOL of bars and packed it into cartons for you,
   cheapest-first. That silently destroyed a real intent: someone buying two
   3-packs as two gifts put in 6 bars and got a single 6-pack. You cannot infer
   carton structure from a bar count, so the customer states it.

   State is now an ARRAY OF BOXES, each with its own size and its own bars:

       [ { size: 3, bars: { 'chip-tease': 2, 'miss-matcha': 1 } },
         { size: 6, bars: { ... } } ]

   Clicking a bar anywhere on the site fills the ACTIVE box — the first one with
   room — and starts a 3-pack if none is open, so the one-click flow from the
   homepage rail and the PDPs is unchanged. Everything else (resize a box, add
   another, remove one) happens in the drawer where there is space to explain it.

   BECAUSE BOXES ARE EXPLICIT, EACH CARTON NOW HAS A REAL MIX. So each box goes
   to Shopify as its own line item carrying its own contents — which is what the
   pick-and-pack team needs and what the old pooled model could not express.
   (The old code said not to add a per-carton breakdown "unless the builder
   itself gains per-box picking". It just did.)

   HOW AN ORDER IS BUILT
     * one `Chunky Box — 3/6 Bars` line per box ($35 / $65), carrying a
       `Box contents` property naming that box's mix. Two identical boxes merge
       into quantity 2 on their own, which is correct.
     * one $0.00 line per FLAVOUR, quantity summed across every box, so
       per-flavour inventory depletes and the packing slip names every bar.
     Same ghost-line pattern the store already uses for giftbox_ghost_product.

   MARKUP HOOKS
     [data-add-bar="<slug>"]    add a bar to the active box
     [data-add-variant="<ref>"] add any ordinary variant (id, or a PAPI_VARIANTS
                                key like "smooth" / "3some-2-1"); optional
                                [data-add-qty], [data-qty-from], [data-add-name]
     [data-cart-count]          bag count
     [data-cart-open]           open the drawer

   The theme sets window.PAPI_VARIANTS before this script. WITHOUT it the file
   still runs — the prototype has no Shopify behind it, so checkout falls back
   to a toast rather than throwing.
   ========================================================================== */
(function () {
  'use strict';

  /* v4 = explicit boxes. The v3 value was a flat {slug: qty} pool and cannot be
     read as boxes, so the key is bumped rather than migrated — a stale pool
     would otherwise deserialise into a single malformed box. */
  var KEY = 'papi_chunky_boxes_v4';

  /* Set on the way to /checkout and read on the way back. It is what lets the
     builder tell "they paid" from "they hit the back button", which look
     identical from here otherwise. */
  var PENDING_KEY = 'papi_checkout_pending';

  /* Where the bar thumbnails live. Relative in the prototype; in the Shopify
     theme the templates set this to the CDN asset base, because theme assets
     are not served from a sibling /assets/ path. */
  var ASSETS = window.PAPI_ASSET_BASE || 'assets/';

  /* Shopify variant IDs, injected by the theme. Null in the prototype. */
  var V = window.PAPI_VARIANTS || null;

  /* Single source of truth for the range. Keep in step with the PDPs. */
  var FLAVOURS = [
    { slug: 'chip-tease',      name: 'Chip Tease',      href: '/products/chunky-chip-tease' },
    { slug: 'jelly-nutcase',   name: 'Jelly Nutcase',   href: '/products/chunky-jelly-nutcase' },
    { slug: 'miss-matcha',     name: 'Miss Matcha',     href: '/products/chunky-miss-matcha' },
    { slug: 'pretzel-pleaser', name: 'Pretzel Pleaser', href: '/products/chunky-pretzel-pleaser' }
  ].concat(seasonalFlavours());

  /* SEASONAL BARS (limited editions, one season at a time) are
     declared by the THEME, not here: a season build emits
     window.PAPI_SEASONAL_FLAVOURS before this file runs, and the everyday theme
     emits nothing, so this list is the core four there.

     A seasonal bar joins the range only while it can actually be bought. Two
     reasons it might not be, both fatal if ignored:
       - its product is DRAFT or off the Online Store, so the bridge rendered its
         id as 0. barsLive() needs EVERY flavour live, so one dead seasonal bar
         would switch off the whole builder, core bars included.
       - it is tracked and has sold out (V.barAvail false). Its id is real, so a
         box holding it would be refused at /cart/add.js with nothing to act on.
     Dropping it here also cleans saved boxes: read() discards any flavour not
     in this list, so a box restored with a sold-out seasonal bar comes back one
     short and asks to be refilled rather than checking out without it.
     The prototype has no bridge (V null) and keeps the bar so it can be seen. */
  function seasonalFlavours() {
    var extra = window.PAPI_SEASONAL_FLAVOURS || [];
    return extra.filter(function (f) {
      if (!V) return true;
      if (!V.bars || !V.bars[f.slug]) return false;
      return !(V.barAvail && V.barAvail[f.slug] === false);
    });
  }

  var SIZES = [3, 6];
  var PRICE = { 3: 3500, 6: 6500 };   /* cents, AUD */

  /* Free-shipping thresholds, in cents. These are NOT marketing numbers — they
     are read off the store's real delivery config, and if either moves in
     Shopify it has to move here too or the bar promises something checkout
     won't honour.

     AMBIENT ($65) is the TOTAL_PRICE >= condition now carried by all three AU
     zones on the "Shipping" profile (spreads, 3some, merch) and by the default
     "General profile". Lowered from $100 on 2026-08-17.

     EXPRESS ($120) applies the moment the bag holds a bar, and CANNOT be a rate
     condition. Two reasons, both fatal: the bars are $0.00 ghost lines and the
     box SKUs are requiresShipping:false, so the "Chunky Bars — Express only"
     profile's own subtotal is always $0; and Shopify SUMS the rates of every
     profile in a cart, so a mixed bag (bars + jars) would be measured as two
     separate part-carts and neither would clear its threshold. It is an
     automatic order-level free-shipping discount instead, which sees the whole
     subtotal and zeroes every delivery group at once.
     ** THAT DISCOUNT MUST EXIST BEFORE THIS THEME IS PUBLISHED, or every bar
     bag between $120 and $139 promises free shipping and is charged express. ** */
  /* GIFT WITH PURCHASE — "Free Papi Tee (orders over $100)".
     The discount itself is a store-level automatic BXGY, so it applies on ANY
     theme with no theme code at all. What a theme HAS to do is tell the
     customer, because Shopify never inserts the tee for them: no tee in the
     bag, no discount, and they simply never find out they had earned one.

     Keep $100 in step with the discount in admin. If they disagree, this
     promises a gift checkout will not honour. */
  var GWP_THRESHOLD = 10000;
  var GWP_LINK = '/pages/shop#merch';
  /* Handles, not variant ids — each tee has 4-8 size/colour variants and the
     list would rot the first time a size sells out and is replaced. These are
     the four products named in the discount's "customer gets" set. */
  var GWP_HANDLES = ['papi-tee-copy', 'come-to-papi-tee-copy',
                     'grab-life-by-the-pistachio-nuts-tee-copy',
                     'spread-good-vibes-tee-copy'];

  var FREE_SHIP_AMBIENT = 6500;
  var FREE_SHIP_EXPRESS = 12000;

  var byId = {};
  FLAVOURS.forEach(function (f) { byId[f.slug] = f; });

  /* ---------- state ---------------------------------------------------- */

  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(KEY));
      if (!Array.isArray(raw)) return [];
      var clean = [];
      raw.forEach(function (b) {
        if (!b || SIZES.indexOf(b.size) === -1) return;
        var bars = {}, n = 0;
        Object.keys(b.bars || {}).forEach(function (k) {
          var q = parseInt(b.bars[k], 10);
          /* drop unknown flavours (a renamed slug) and never let a restored box
             hold more than its size */
          if (!byId[k] || !(q > 0)) return;
          q = Math.min(q, b.size - n);
          if (q > 0) { bars[k] = q; n += q; }
        });
        clean.push({ size: b.size, bars: bars });
      });
      return clean;
    } catch (e) { return []; }
  }

  function write() {
    try { localStorage.setItem(KEY, JSON.stringify(boxes)); } catch (e) {}
  }

  var boxes = read();
  var shopLines = [];       /* everything in the Shopify cart that ISN'T ours */
  var cartHasChunky = false;/* ...and whether the ones that ARE ours are there */
  var cartUnbalanced = false;/* our lines are there but bars != carton seats */
  var busy = false;

  function count(b) {
    return Object.keys(b.bars).reduce(function (n, k) { return n + b.bars[k]; }, 0);
  }
  function isFull(b) { return count(b) === b.size; }
  function totalBars() {
    return boxes.reduce(function (n, b) { return n + count(b); }, 0);
  }
  function money(cents) {
    return '$' + (cents / 100).toFixed(cents % 100 ? 2 : 0);
  }

  /* The box a bar click lands in: the first one with room. */
  /* Carton stock. 500 printed 3-packs and 500 6-packs against 2,000 of each
     bar, so a carton is what actually runs out. Both box SKUs are tracked on
     DENY, so checkout is already safe without this — what this adds is telling
     the customer BEFORE they pick six bars and hit a 422 they cannot act on.
     Absent map (the prototype has no Shopify behind it) means "available", so
     the builder still works with no bridge. */
  function boxAvailable(size) {
    var m = V && V.boxAvail;
    if (!m) return true;
    var v = m[String(size)];
    return v === undefined ? true : !!v;
  }

  /* CAN A BAR BE BOUGHT AT ALL RIGHT NOW.
     A bar is never sold out on its own — it is sold out because every carton
     it could ride in has gone. One size still open is enough: if the 6-packs
     are out but 3-packs are not, "Add bar" still works and must not be greyed.
     Only when BOTH cartons are gone does the bar become unbuyable, and that is
     the state the whole out-of-stock experience keys off. */
  function barsBuyable() {
    /* A SEASONAL bar's own page also goes out when that one bar has (see
       seasonalFlavours) — the cartons may be fine, but this flavour is not.
       Only seasonal PDPs carry data-papi-bar, so every other page is
       unaffected and still keys off the cartons alone. */
    var pageBar = document.documentElement.getAttribute('data-papi-bar');
    if (pageBar && !byId[pageBar]) return false;
    for (var i = 0; i < SIZES.length; i++) {
      if (boxAvailable(SIZES[i])) return true;
    }
    return false;
  }

  /* The size a first click should open. Normally the 3-pack — upgrading is one
     click and starting big over-commits someone who wanted three — but if the
     3-packs have gone, start the 6 rather than opening a carton that cannot be
     bought. */
  function defaultSize() {
    for (var i = 0; i < SIZES.length; i++) {
      if (boxAvailable(SIZES[i])) return SIZES[i];
    }
    return null;
  }

  function activeIndex() {
    for (var i = 0; i < boxes.length; i++) if (!isFull(boxes[i])) return i;
    return -1;
  }

  function boxesPrice() {
    return boxes.reduce(function (n, b) { return n + PRICE[b.size]; }, 0);
  }

  /* Boxes the customer has actually started. An OPENED BUT EMPTY box must not
     count toward free shipping — a carton's price is fixed by its size, so an
     untouched 6-pack would otherwise credit $65 of progress against nothing. */
  function committedBoxesPrice() {
    return boxes.reduce(function (n, b) { return count(b) ? n + PRICE[b.size] : n; }, 0);
  }

  /* Which threshold this bag is measured against. One bar anywhere in the bag
     makes the WHOLE order express — chocolate cannot ride standard — so the
     higher number applies to the entire subtotal, not just the bar part. */
  /* Does the bag contain chocolate, wherever it came from?

     `totalBars()` alone is not the answer, and using it was a real bug: it only
     sees the LOCAL box builder, so a bundle sitting in the Shopify cart counted
     as ambient. A bag holding Bars + Spread ($44) and two jars reaches $68, and
     the drawer would announce free shipping at the $65 ambient threshold while
     checkout charged $15 — because anything with chocolate in it needs $120. The
     drawer must never promise a number checkout will not honour.

     Three places chocolate can hide: the local builder, a carton or loose bar
     already posted to Shopify, and a bundle wrapper. The Papi Gift Set is
     deliberately excluded — it is a 3some and a candle, both shelf stock. */
  function bagHasChunky() {
    if (totalBars() > 0) return true;
    if (!V) return false;

    var ids = chunkyVariants();
    var w = V.bundleWrappers || {};
    Object.keys(w).forEach(function (handle) {
      // bundleCarton() returns null for a bundle with no bars in it.
      if (w[handle] && bundleCarton(handle) !== null) ids.push(Number(w[handle].id));
    });

    return shopLines.some(function (l) { return ids.indexOf(Number(l.variant_id)) !== -1; });
  }

  function shipThreshold() {
    return bagHasChunky() ? FREE_SHIP_EXPRESS : FREE_SHIP_AMBIENT;
  }

  /* What Shopify will actually measure the threshold against: the cart SUBTOTAL
     — before shipping, after discounts. For a Chunky order that is the box lines
     only, because the bar lines are $0.00 ghost lines carrying the flavours;
     the boxes carry the money. Counting bars here would promise free shipping
     that never arrives. */
  function shipProgress() {
    var target = shipThreshold();
    var spent = committedBoxesPrice() +
      shopLines.reduce(function (n, l) { return n + l.line_price; }, 0);
    return {
      spent: spent,
      target: target,
      express: bagHasChunky(),
      remaining: Math.max(0, target - spent),
      unlocked: spent >= target,
      pct: Math.min(100, Math.round(spent / target * 100))
    };
  }

  /* Progress toward the free tee. The tee's OWN price is excluded from the
     spend: it is the reward, not part of qualifying for it, and counting it
     would tell someone with a $58 tee and nothing else that they had already
     earned their gift. */
  function gwpStatus() {
    var hasTee = false, spent = committedBoxesPrice();
    shopLines.forEach(function (l) {
      if (GWP_HANDLES.indexOf(l.handle) !== -1) { hasTee = true; return; }
      spent += l.line_price;
    });
    return {
      spent: spent,
      hasTee: hasTee,
      remaining: Math.max(0, GWP_THRESHOLD - spent),
      unlocked: spent >= GWP_THRESHOLD
    };
  }

  /* Readable mix, e.g. "2x Chip Tease, 1x Miss Matcha". Ordered by FLAVOURS so
     two boxes with the same contents produce the SAME string and merge into one
     Shopify line at quantity 2 instead of two lines that look like a bug. */
  function contents(b) {
    return FLAVOURS.filter(function (f) { return b.bars[f.slug]; })
      .map(function (f) { return b.bars[f.slug] + '× ' + f.name; })
      .join(', ');
  }

  /* The gate. Every box must be exactly full, and there must be at least one. */
  function status() {
    var n = boxes.length;
    var short = boxes.reduce(function (t, b) { return t + (b.size - count(b)); }, 0);
    var s = { boxes: n, bars: totalBars(), short: short,
              valid: n > 0 && short === 0, price: boxesPrice(),
              tone: 'building', message: '' };

    if (!n) {
      s.message = 'Start a box: any 3 bars for $35, or any 6 for $65.';
    } else if (s.valid) {
      s.tone = 'ready';
      s.message = n === 1
        ? 'Box full — ' + money(s.price) + '. Add another if you are feeling brave.'
        : n + ' boxes, all full — ' + money(s.price) + '.';
    } else {
      s.message = 'Add ' + short + ' more ' + (short === 1 ? 'bar' : 'bars') +
                  ' to fill ' + (n === 1 ? 'your box' : 'your boxes') + '.';
    }
    return s;
  }

  function bagCount() {
    return totalBars() + shopLines.reduce(function (n, l) { return n + l.quantity; }, 0);
  }

  /* ---------- Shopify cart --------------------------------------------- */

  function chunkyVariants() {
    if (!V) return [];
    var ids = [V.box3, V.box6];
    FLAVOURS.forEach(function (f) { if (V.bars && V.bars[f.slug]) ids.push(V.bars[f.slug]); });
    return ids.filter(Boolean).map(Number);
  }

  /* Are the box SKUs and every bar purchasable yet? The theme emits 0 for any
     product it cannot see (draft, or off the Online Store channel), so this is
     false until the whole set is live. Checked before checkout rather than
     letting /cart/add.js fail on a variant id of 0. */
  function barsLive() {
    if (!V || !V.bars || !V.box3 || !V.box6) return false;
    return FLAVOURS.every(function (f) { return !!V.bars[f.slug]; });
  }

  function postJSON(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) throw new Error(j && (j.description || j.message) || ('Cart error ' + r.status));
        return j;
      });
    });
  }

  function pullCart() {
    if (!V) return Promise.resolve();
    var mine = chunkyVariants();
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var all = c.items || [];
        shopLines = all.filter(function (l) { return mine.indexOf(l.variant_id) === -1; });
        // Kept because reconcile() needs it: our chunky lines are deliberately
        // hidden from the drawer, so their absence is otherwise unobservable.
        cartHasChunky = all.some(function (l) { return mine.indexOf(l.variant_id) !== -1; });
        // Bars against the seats the cartons provide. Bundle wrappers are not
        // expanded in /cart.js, so only box-builder lines are counted here.
        var seats = 0, bars = 0;
        all.forEach(function (l) {
          if (mine.indexOf(l.variant_id) === -1) return;
          if (l.variant_id === Number(V.box3)) seats += 3 * l.quantity;
          else if (l.variant_id === Number(V.box6)) seats += 6 * l.quantity;
          else bars += l.quantity;
        });
        cartUnbalanced = seats !== bars;
      })
      .catch(function () { /* leave shopLines as-is; the drawer is still usable */ });
  }

  /* Buttons name a variant either by raw numeric ID or by a key the theme
     published on PAPI_VARIANTS.products — "smooth", "crunchy", a 3some mix. */
  function resolveVariant(ref) {
    if (!ref) return null;
    if (/^\d+$/.test(ref)) return ref;
    return (V && V.products && V.products[ref]) || null;
  }

  /* ---------- the bundle guard ------------------------------------------

     A bundle wrapper is a PRICE WITH NO GOODS ATTACHED. What puts goods on the
     order is the cart transform, and it only fires when the wrapper line
     carries a valid `_papi_picks`. With no picks it does nothing and says
     nothing: the wrapper checks out at the full bundle price, deducts no
     inventory, and reaches the packing bench as an empty box. The wrappers are
     deliberately untracked (tracked ones returned 422 on every add), so there
     is no stock ceiling to catch it either — it fails all the way to a paying
     customer.

     This sits on the single post path rather than at each call site. All three
     were live routes to the same mistake: the shop tile added through
     addVariant, the PDP picker through addLines, and the box checkout builds
     its own item list. Guarding one of them would have left the other two. */
  function bundleFor(id) {
    var w = V && V.bundleWrappers;
    if (!w) return null;
    for (var handle in w) {
      if (w[handle] && Number(w[handle].id) === Number(id)) {
        return { handle: handle, url: w[handle].url || ('/products/' + handle) };
      }
    }
    return null;
  }

  /* A bundle ships in one of the printed cartons, so it cannot outlive them.
     Since the cart transform now emits the box SKU as a component (that is what
     actually deducts a carton), a bundle bought after the cartons ran out would
     expand onto an out-of-stock deny-policy variant and die at payment. Far
     better to grey the tile out. Which carton follows from the pick spec: three
     bars go in a 3-pack, six in a 6-pack. */
  function bundleCarton(handle) {
    var spec = V && V.bundles && V.bundles[handle];
    var picks = spec && spec.picks;
    if (!picks) return null;
    for (var i = 0; i < picks.length; i++) {
      if (picks[i].kind === 'bars') return picks[i].count > 3 ? 6 : 3;
    }
    return null;                      // the gift set holds no bars, so no carton
  }

  function cartonGone(handle) {
    var sz = bundleCarton(handle);
    return sz !== null && !boxAvailable(sz);
  }

  /* The fixed merch inside a bundle (the Gift Set's candle, The Lot's apron)
     is real stock as well. The cart transform expands the wrapper onto it at
     add time, so once it hits zero Shopify refuses the whole add with "already
     sold out" — which surfaced as a dead Add button on the Gift Set on
     2026-09-21 while the candle sat at 0. V.avail comes off the same bridge as
     boxAvail; missing means "assume fine", same as the carton rule. */
  function fixedGone(handle) {
    var spec = V && V.bundles && V.bundles[handle];
    var fixed = spec && spec.fixed;
    if (!fixed || !V.avail) return null;
    for (var i = 0; i < fixed.length; i++) {
      if (V.avail[fixed[i][0]] === false) return fixed[i][2] || fixed[i][0];
    }
    return null;
  }

  /* Why a bundle cannot be bought right now, as a phrase for the customer, or
     null when it can. One rule for the shop tiles and the add guard. */
  function bundleGone(handle) {
    if (cartonGone(handle)) return 'the box it ships in has run out';
    var f = fixedGone(handle);
    return f ? 'the ' + f + ' that comes in it has run out' : null;
  }

  function paintBundleStock() {
    /* The homepage pack picker. It is the first thing anyone sees, so a size
       running out has to show HERE and not only once they open the drawer. Same
       boxAvail map as the builder and the bundle tiles — one source of truth, so
       the three surfaces cannot contradict each other.
       The card stays clickable on purpose: addBox() refuses and flashes "the
       3-pack box is sold out", which tells them why. A dead button would not. */
    Array.prototype.forEach.call(
      document.querySelectorAll('.chunky__pick[data-addbox]'), function (pick) {
        var size = Number(pick.getAttribute('data-addbox'));
        if (boxAvailable(size)) return;
        pick.classList.add('chunky__pick--out');
        var btn = pick.querySelector('.chunky__tier');
        if (btn) btn.setAttribute('aria-disabled', 'true');
      });

    var w = V && V.bundleWrappers;
    if (!w) return;
    Object.keys(w).forEach(function (handle) {
      if (!bundleGone(handle)) return;
      var tiles = document.querySelectorAll('[data-bundle-tile="' + handle + '"]');
      Array.prototype.forEach.call(tiles, function (tile) {
        tile.classList.add('mcard--out');
        var cta = tile.querySelector('.mcard__go');
        if (cta) {
          var out = document.createElement('span');
          out.className = 'mcard__go mcard__go--out';
          out.textContent = 'Sold out';
          cta.parentNode.replaceChild(out, cta);
        }
        /* SOLD-OUT BUNDLES SINK TO THE BOTTOM OF THE GRID.
           Someone scanning the bundles row is deciding what to buy TODAY, and
           the two they cannot have were sitting first and third. Re-appending
           is enough — the grid is source-ordered, so moving the node moves the
           tile, and the ones still buyable close ranks at the front.

           Done here rather than in Liquid because the server cannot know: the
           bundles are untracked, so `p.available` is true even when the carton
           inside them has gone. Carton stock is the real signal and it only
           exists client-side. */
        if (tile.parentNode) tile.parentNode.appendChild(tile);
      });
    });
  }

  /* THE BAR OUT-OF-STOCK STATE, painted onto every surface at once.
     Declarative on purpose: the generators emit BOTH states into the markup
     and this decides which is real, so a new page only has to carry the right
     attributes rather than its own copy of this logic.

       [data-bar-card]      a bar tile/card anywhere      -> gets .is-out
       [data-add-bar]       any add button                -> disabled + relabelled
       [data-sold-out-only] the waitlist block, badge     -> revealed
       [data-in-stock-only] price, add button wrapper     -> hidden

     Painted rather than server-rendered because carton stock changes between
     page cache and page view — the same reason the homepage stamp works this
     way. The cost is a brief flash of the in-stock state on a cold load, which
     the CSS covers by starting [data-sold-out-only] hidden. */
  /* A seasonal bar's tiles and cards elsewhere on the site (other bar PDPs,
     the shop, the homepage) carry data-seasonal-card="<slug>". While that bar
     is out of the range (draft, or sold out — see seasonalFlavours) the card is
     REMOVED rather than greyed: its add button would find no flavour and do
     nothing, and a sold-out limited edition is not something to keep
     advertising between the core bars. Its own PDP handles itself. */
  function paintSeasonal() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-seasonal-card]'), function (el) {
        if (!byId[el.getAttribute('data-seasonal-card')]) el.parentNode.removeChild(el);
      });
  }

  function paintBarStock() {
    var out = !barsBuyable();
    var root = document.documentElement;
    root.classList.toggle('papi-bars-out', out);
    root.classList.toggle('papi-bars-in', !out);

    Array.prototype.forEach.call(
      document.querySelectorAll('[data-sold-out-only]'), function (el) {
        el.hidden = !out;
      });
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-in-stock-only]'), function (el) {
        el.hidden = out;
      });
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-bar-card]'), function (el) {
        el.classList.toggle('is-out', out);
      });

    if (!out) return;
    /* The add buttons are the one thing that must not merely LOOK dead. The
       chip in the drawer, the rail on the homepage and the three buttons on a
       bar PDP all carry data-add-bar and all route into addBar(), so disabling
       them here covers every one without naming a single class. */
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-add-bar]'), function (btn) {
        if (btn.closest && btn.closest('[data-keep-enabled]')) return;
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is-out');
        if (btn.tagName === 'BUTTON') btn.disabled = true;
      });
  }

  function postAdd(items) {
    for (var i = 0; i < items.length; i++) {
      var b = bundleFor(items[i].id);
      if (!b) continue;
      var gone = bundleGone(b.handle);
      if (gone) {
        flash('That one\u2019s sold out \u2014 ' + gone + '.');
        var goneErr = new Error('Carton sold out.');
        goneErr.papiHandled = true;
        return Promise.reject(goneErr);
      }
      var props = items[i].properties;
      if (props && props._papi_picks) continue;
      /* Refusing alone would read as a broken button, so send them to the one
         page where the picks can actually be made. */
      flash('Pick what goes in it first — taking you there.');
      (function (url) {
        window.setTimeout(function () { window.location.href = url; }, 900);
      }(b.url));
      var err = new Error('Bundle needs its picks.');
      err.papiHandled = true;          // the callers' catch stays quiet
      return Promise.reject(err);
    }
    return postJSON('/cart/add.js', { items: items });
  }

  /* Add any ordinary variant (spread, 3some, merch) straight to Shopify.
     Accepts a numeric id OR a key — resolution happens HERE, not at the call
     site, because the 3some page calls this directly with a mix key. */
  function addVariant(ref, qty, props, label) {
    if (!V) { flash('Prototype — checkout is not wired up yet.'); return Promise.resolve(); }
    var id = resolveVariant(ref);
    if (!id) { flash('That one isn’t available right now.'); return Promise.resolve(); }
    if (busy) return Promise.resolve();
    busy = true; render();
    var item = { id: Number(id), quantity: qty || 1 };
    if (props) item.properties = props;
    return postAdd([item])
      .then(pullCart)
      .then(function () {
        busy = false; render();
        flash((label || 'Added') + ' — in your bag');
        openDrawer();
      })
      .catch(function (e) {
        busy = false; render();
        if (e && e.papiHandled) return;
        flash(e.message || 'Could not add that, sorry.');
      });
  }

  /* Add SEVERAL lines in ONE request — the bundles.

     A bundle is a price wrapper plus its contents, exactly like the Chunky box:
     one line carrying the money and one $0.00 ghost line per component, so the
     component SKUs are what actually deplete. It has to be a single /cart/add.js
     call, not a loop of addVariant(): each call is a separate request, Shopify
     rate-limits the cart endpoint hard (a plain loop of five returned 429 on the
     second), and a partial failure would leave a customer holding a paid wrapper
     with none of the goods in it. One request either lands whole or not at all.

     Every id is checked BEFORE posting for the same reason — a component that is
     not published yet resolves to 0 through PAPI_VARIANTS' `| default: 0`, and
     posting a 0 would have Shopify reject the whole batch with a message that
     names nothing the customer can act on. */
  function addLines(items, label) {
    if (!V) { flash('Prototype — checkout is not wired up yet.'); return Promise.resolve(); }
    if (busy) return Promise.resolve();
    var clean = [], bad = false;
    (items || []).forEach(function (it) {
      var id = Number(resolveVariant(it.id));
      if (!id || !(it.quantity > 0)) { bad = true; return; }
      var line = { id: id, quantity: it.quantity };
      if (it.properties) line.properties = it.properties;
      clean.push(line);
    });
    if (bad || !clean.length) {
      flash('That bundle isn\u2019t available right now.');
      return Promise.resolve();
    }
    busy = true; render();
    return postAdd(clean)
      .then(pullCart)
      .then(function () {
        busy = false; render();
        flash((label || 'Added') + ' \u2014 in your bag');
        openDrawer();
      })
      .catch(function (e) {
        busy = false; render();
        if (e && e.papiHandled) return;
        flash(e.message || 'Could not add that, sorry.');
      });
  }

  /* Remove every box-builder line from the Shopify cart, BY LINE KEY.

     This used to post `updates: { <variantId>: 0 }`. Shopify's update.js, keyed
     by variant id, changes only the FIRST line carrying that variant — and two
     cartons of the same size with different mixes are two lines of one variant.
     So a customer who backed out of checkout and went again kept one stale
     carton line; the re-add then merged an identical carton into it as
     quantity 2. They were charged for a box they never built, with no bars
     behind it. Order #50009 (28 Sep 2026) and nine others, found 8 Oct 2026.

     A line key names exactly one line, so nothing survives. Keys must be sent
     as strings. */
  function clearChunky() {
    var mine = chunkyVariants();
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var updates = {}, n = 0;
        (c.items || []).forEach(function (l) {
          if (mine.indexOf(l.variant_id) === -1) return;
          updates[String(l.key)] = 0; n++;
        });
        return n ? postJSON('/cart/update.js', { updates: updates }) : null;
      });
  }

  /* A cart whose bars do not match its cartons cannot be bought — the bar
     guard refuses it — and it can only be a leftover: the builder in
     localStorage is the source of truth and checkout() rebuilds these lines
     from it every time. So drop the leftovers rather than leave the customer
     with a bag that rejects whatever they add next. Runs AFTER reconcile(),
     which reads cartHasChunky to tell "paid" from "backed out". */
  function healCart() {
    if (!V || !cartUnbalanced) return Promise.resolve();
    return clearChunky().then(pullCart).catch(function () {});
  }

  function changeLine(key, qty) {
    if (!V || busy) return;
    busy = true; render();
    postJSON('/cart/change.js', { id: key, quantity: qty })
      .then(pullCart)
      .then(function () { busy = false; render(); })
      .catch(function (e) { busy = false; render(); flash(e.message || 'Could not update that.'); });
  }

  /* Checkout: reconcile, then go.
     1. zero out any chunky lines already in the Shopify cart, so coming back
        from checkout and going again cannot double an order;
     2. add one line per box (with its contents) and one $0 line per flavour;
     3. hand over to Shopify.
     Step 1 targets OUR variants only — it never touches a spread, a 3some or a
     tee the customer put in the bag. */
  function checkout() {
    var s = status();
    if (!V) { flash('Prototype — checkout is not wired up yet.'); return; }
    if (s.boxes && !barsLive()) {
      flash('The Chunky bars aren’t on sale just yet — checkout opens with them.');
      return;
    }
    if (s.boxes && !s.valid) return;
    if (!s.boxes && !shopLines.length) return;
    if (busy) return;
    busy = true; render();

    clearChunky()
      .then(function () {
        if (!s.boxes) return null;
        var items = [];

        /* The money AND the picking instruction: one line per carton, each
           naming exactly what goes in it. */
        boxes.forEach(function (b) {
          items.push({
            id: Number(b.size === 6 ? V.box6 : V.box3),
            quantity: 1,
            /* `Delivery` rides on the carton, not the bars, so it appears ONCE
               per box in the checkout summary rather than on every loose bar.
               Line item properties are the only way to say anything inside
               checkout without a Plus plan — UI extensions on the payment and
               shipping steps are Plus-only. This is the last place a customer
               can learn about the wait BEFORE they pay. */
            properties: {
              'Box contents': contents(b),
              'Delivery': 'Made fresh to order \u2014 allow 1\u20132 weeks'
            }
          });
        });

        /* The goods. $0 each, inventory-tracked, summed across every box. */
        var perFlavour = {};
        boxes.forEach(function (b) {
          Object.keys(b.bars).forEach(function (k) {
            perFlavour[k] = (perFlavour[k] || 0) + b.bars[k];
          });
        });
        FLAVOURS.forEach(function (f) {
          if (!perFlavour[f.slug]) return;
          items.push({
            id: Number(V.bars[f.slug]),
            quantity: perFlavour[f.slug],
            properties: { 'Chunky box bar': f.name }
          });
        });
        return postAdd(items);
      })
      .then(function () {
        /* The builder is NOT cleared here any more. It used to be, to stop a
           back-button return showing a box that was already in the cart — but
           step 1 above already zeroes our lines before re-adding them, so the
           whole flow is idempotent and the clear was redundant. What it did do
           was destroy the customer's mix the moment they backed out of
           checkout, which is a lot worse than the problem it solved.
           reconcile() clears it later, once we can tell the order went through. */
        try { localStorage.setItem(PENDING_KEY, '1'); } catch (e) {}
        window.location.href = '/checkout';
      })
      .catch(function (e) {
        busy = false; render();
        flash(e.message || 'Checkout could not start. Try again?');
      });
  }

  /* ---------- box operations ------------------------------------------- */

  function addBox(size, quiet) {
    if (!boxAvailable(size)) {
      flash('The ' + size + '-pack box is sold out.');
      return -1;
    }
    boxes.push({ size: size, bars: {} });
    write(); render();
    if (!quiet) { flash(size + '-pack started — pick your ' + size + '.'); openDrawer(); }
    return boxes.length - 1;
  }

  function removeBox(i) {
    boxes.splice(i, 1); write(); render();
  }

  /* Resizing is only offered when it cannot destroy a choice: you can always
     grow a box, and you can only shrink one that would still hold its bars. */
  function canResize(b, size) {
    return size !== b.size && count(b) <= size && boxAvailable(size);
  }

  function setBoxSize(i, size) {
    if (!canResize(boxes[i], size)) return;
    boxes[i].size = size; write(); render();
  }

  function addBar(slug, n) {
    if (!byId[slug]) return;
    n = n || 1;
    for (var k = 0; k < n; k++) {
      var i = activeIndex();
      /* No box open: start the small one. Upgrading to a 6-pack is one click in
         the drawer, and starting big would over-commit someone who wanted 3. */
      if (i === -1) {
        var start = defaultSize();
        if (start === null) { flash('Chunky boxes are sold out.'); return; }
        i = addBox(start, true);
      }
      if (i === -1) return;
      var b = boxes[i];
      b.bars[slug] = (b.bars[slug] || 0) + 1;
    }
    write(); render();
    var s = status();
    flash(s.valid ? byId[slug].name + ' in — ' + (s.boxes === 1 ? 'box full' : 'all boxes full')
                  : byId[slug].name + ' in — ' + s.short + ' to go');
    openDrawer();
  }

  function setQty(i, slug, n) {
    var b = boxes[i];
    if (!b) return;
    if (n <= 0) { delete b.bars[slug]; }
    else {
      var others = count(b) - (b.bars[slug] || 0);
      b.bars[slug] = Math.max(1, Math.min(n, b.size - others));
    }
    write(); render();
  }

  /* ---------- styles ---------------------------------------------------- */

  var CSS = [
    '.pcart-veil{position:fixed;inset:0;z-index:900;background:rgb(20 22 15 / .55);opacity:0;visibility:hidden;transition:opacity .4s ease,visibility .4s;backdrop-filter:blur(2px)}',
    '.pcart-veil.on{opacity:1;visibility:visible}',
    '.pcart{position:fixed;inset:0 0 0 auto;z-index:901;width:min(46rem,100%);display:flex;flex-direction:column;background:var(--papi-shell,#f9f9dd);color:var(--papi-green-dark,#3e4728);transform:translateX(103%);transition:transform .5s cubic-bezier(.16,1,.3,1);box-shadow:-2rem 0 6rem rgb(20 22 15 / .3)}',
    '.pcart.on{transform:translateX(0)}',
    '@media (prefers-reduced-motion:reduce){.pcart,.pcart-veil{transition:none}}',
    '.pcart__head{display:flex;align-items:center;gap:1.4rem;padding:2.2rem 2.4rem 1.6rem;border-bottom:.2rem solid rgb(62 71 40 / .14)}',
    '.pcart__head h2{font-family:var(--papi-font-display,sans-serif);font-weight:860;text-transform:uppercase;font-size:2.4rem;line-height:1;margin:0}',
    '.pcart__close{margin-left:auto;width:3.8rem;height:3.8rem;border-radius:50%;display:grid;place-items:center;font-size:2.2rem;line-height:1;color:inherit;background:rgb(62 71 40 / .08);transition:background-color .3s,transform .3s}',
    '.pcart__close:hover{background:rgb(62 71 40 / .18);transform:rotate(90deg)}',
    /* min-height:0 is load-bearing: a flex item defaults to min-height:auto,
       which refuses to shrink below its content and pushes the foot off-screen
       instead of scrolling. -webkit-overflow-scrolling gives iOS momentum. */
    '.pcart__body{flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;padding:2rem 2.4rem 1rem}',

    /* progress pips — one per bar in the biggest box, filled to the count */
    '.pcart__meter{display:flex;gap:.5rem;margin:0 0 1.4rem}',
    '.pcart__meter i{flex:1;height:.6rem;border-radius:99rem;background:rgb(62 71 40 / .14);transition:background-color .35s ease}',
    '.pcart__meter i.f{background:var(--papi-green,#8e9839)}',
    '.pcart__meter i.g{box-shadow:inset 0 0 0 .2rem var(--papi-green-dark,#3e4728)}',
    '.pcart__msg{font-size:1.5rem;line-height:1.5;margin:0 0 2rem;font-weight:500}',
    '.pcart[data-tone="ready"] .pcart__msg{font-weight:700}',
    '.pcart[data-tone="over"] .pcart__msg{color:#a3311f;font-weight:700}',

    /* line items */
    '.pcart__lines{list-style:none;margin:0;padding:0;display:grid;gap:1.2rem}',
    '.pcart-line{display:grid;grid-template-columns:6.4rem 1fr auto;gap:1.4rem;align-items:center;background:#fff6;border:.2rem solid rgb(62 71 40 / .12);border-radius:1.6rem;padding:1rem}',
    '.pcart-line__sw{width:6.4rem;height:6.4rem;border-radius:1.1rem;overflow:hidden;position:relative;display:grid;place-items:center;background:rgb(62 71 40 / .07)}',
    /* contain — a square cover-crop of an angled pack shot reads as a smear */
    '.pcart-line__sw img{width:100%;height:100%;object-fit:contain;padding:8%;box-sizing:border-box}',
    '.pcart-line__nm{font-family:var(--papi-font-display,sans-serif);font-weight:850;text-transform:uppercase;font-size:1.8rem;line-height:1;display:block}',
    '.pcart-line__sub{font-size:1.25rem;opacity:.65;display:block;margin-top:.4rem}',
    '.pcart-step{display:inline-flex;align-items:center;border:.2rem solid var(--papi-green-dark,#3e4728);border-radius:99rem;overflow:hidden}',
    '.pcart-step button{width:3.2rem;height:3.2rem;font-family:var(--papi-font-display,sans-serif);font-weight:800;font-size:1.8rem;color:inherit;display:grid;place-items:center}',
    '.pcart-step button:hover{background:rgb(62 71 40 / .12)}',
    '.pcart-step span{min-width:2.4rem;text-align:center;font-weight:700;font-size:1.6rem}',

    '.pcart__empty{text-align:center;padding:2rem 0 1rem;font-size:1.5rem;opacity:.7}',

    /* add-more strip — you add a bar by clicking the bar, not its name */
    '.pcart__more{margin:2.4rem 0 0}',
    '.pcart__more h3{font-family:var(--papi-font-display,sans-serif);font-weight:850;text-transform:uppercase;font-size:1.5rem;letter-spacing:.06em;margin:0 0 1rem;opacity:.75}',
    '.pcart__chips{display:grid;grid-template-columns:1fr 1fr;gap:1rem;list-style:none;padding:0;margin:0}',
    '.pcart-chip{display:grid;gap:.6rem;width:100%;text-align:center;border:.2rem solid rgb(62 71 40 / .18);border-radius:1.4rem;padding:1rem .8rem .9rem;color:inherit;background:rgb(255 255 255 / .35);transition:border-color .25s,transform .25s,background-color .25s}',
    '.pcart-chip:hover{transform:translateY(-.3rem);border-color:var(--papi-green-dark,#3e4728);background:#fff9}',
    '.pcart-chip img{width:100%;height:auto;display:block;pointer-events:none}',
    '.pcart-chip span{font-weight:700;font-size:1.25rem;line-height:1.2}',

    '.pcart__foot{border-top:.2rem solid rgb(62 71 40 / .14);padding:1.8rem 2.4rem 2.4rem;display:grid;gap:1.2rem}',
    '.pcart__tot{display:flex;align-items:baseline;justify-content:space-between;font-family:var(--papi-font-display,sans-serif);font-weight:850;font-size:2.4rem;text-transform:uppercase}',
    '.pcart__tot small{font-family:var(--papi-font-body,sans-serif);font-weight:500;font-size:1.3rem;opacity:.6;text-transform:none}',
    '.pcart__go{width:100%;border-radius:99rem;padding:1.7rem 2rem;font-family:var(--papi-font-display,sans-serif);font-weight:850;text-transform:uppercase;font-size:1.9rem;letter-spacing:.02em;background:var(--papi-green-dark,#3e4728);color:var(--papi-shell,#f9f9dd);transition:background-color .3s,transform .25s}',
    '.pcart__go:hover:not(:disabled){background:var(--papi-green,#8e9839);transform:translateY(-.2rem)}',
    '.pcart__go:disabled{opacity:.32;cursor:not-allowed}',
    '.pcart__note{font-size:1.2rem;opacity:.6;text-align:center;margin:0}',

    /* nav bubble */
    /* outlined, not knocked-out — the nav text colour changes per page and per
       scroll zone, so a filled badge ends up cream-on-cream somewhere */
    '.pcart-badge{display:inline-grid;place-items:center;min-width:2.4rem;height:2.4rem;padding:0 .6rem;border-radius:99rem;border:.2rem solid currentColor;font-size:1.2rem;font-weight:800;margin-left:.6rem;vertical-align:middle;line-height:1}',
    '.pcart-badge b{color:inherit;font-weight:800}',
    '.pcart-badge.ready{animation:pcartpop .5s cubic-bezier(.34,1.56,.64,1)}',
    '@keyframes pcartpop{0%{transform:scale(1)}45%{transform:scale(1.35)}100%{transform:scale(1)}}',

    /* toast */
    '.pcart-toast{position:fixed;left:50%;bottom:2.6rem;z-index:902;transform:translate(-50%,150%);background:var(--papi-green-dark,#3e4728);color:var(--papi-shell,#f9f9dd);border-radius:99rem;padding:1.2rem 2.2rem;font-weight:700;font-size:1.5rem;box-shadow:0 1.2rem 3rem rgb(20 22 15 / .35);transition:transform .45s cubic-bezier(.16,1,.3,1)}',
    '.pcart-toast.on{transform:translate(-50%,0)}',

    '@media (max-width:560px){.pcart__chips{grid-template-columns:1fr}}'
  ].join('\n');
  /* Rules for the real cart and the explicit-box builder. Appended so the block
     above stays byte-identical to the version that was signed off. */
  CSS += [
    '',
    /* ---- bars-out promo, the block that replaces the box builder ----------
       Loud on purpose. It is standing where the builder stood, so anything
       quieter reads as an empty slot rather than an offer. */
    '.pcart__promo{margin:.4rem 0 0;padding:1.8rem 2rem;border-radius:1.8rem;background:var(--papi-green-dark,#3e4728);color:var(--papi-shell,#f9f9dd);display:grid;gap:.7rem}',
    '.pcart__promo-h{margin:0;font-family:var(--papi-font-display,sans-serif);font-weight:850;text-transform:uppercase;font-size:1.6rem;line-height:1.15;letter-spacing:.01em}',
    '.pcart__promo-l{margin:0;font-size:1.4rem;line-height:1.45}',
    '.pcart__promo-l b{font-weight:800}',
    /* Underlined, not colour-shifted: the bright green sits at 2.4:1 on this
       dark green, which fails contrast for a link people are meant to click. */
    '.pcart__promo-l a{color:inherit;text-decoration:underline;text-underline-offset:.25em}',

    /* ---- bar sold-out, shared across every surface -----------------------
       Hidden by default so a cold load never flashes the waitlist form at a
       customer who can buy the thing. paintBarStock() reveals it. */
    /* [hidden] IS NOT ENOUGH ON ITS OWN. The UA stylesheet sets
       [hidden]{display:none}, but any author rule with a real display wins on
       specificity — .buybox__actions{display:flex} kept the add-to-cart button
       on screen on a sold-out PDP even with the attribute set. Every toggled
       block on this site is display-styled, so the override has to be global
       and it has to be !important. */
    '[hidden]{display:none!important}',
    '[data-sold-out-only]{display:none}',
    '.papi-bars-out [data-sold-out-only]:not([hidden]){display:block}',
    '[data-add-bar].is-out{opacity:.4;cursor:not-allowed;filter:grayscale(.7)}',
    '[data-bar-card].is-out{position:relative}',
    '[data-bar-card].is-out .bcard__pic,[data-bar-card].is-out .brow__pic{filter:grayscale(.75) opacity(.5)}',

    /* The badge. Same shape language as the homepage carton stamp — rotated
       pill, shell on dark green — so a customer who saw it there reads it
       instantly here. Smaller, because a card is not a hero. */
    '.bar-out__badge{position:absolute;top:1rem;left:1rem;z-index:3;transform:rotate(-7deg);font-family:var(--papi-font-accent,cursive);font-size:1.5rem;line-height:1;white-space:nowrap;pointer-events:none;color:var(--papi-shell,#f9f9dd);background:var(--papi-green-dark,#3e4728);padding:.6rem 1.2rem;border-radius:99rem;border:.2rem solid var(--papi-shell,#f9f9dd);box-shadow:.25rem .3rem 0 rgb(35 42 20 / .3)}',
    '.bar-out__next{display:block;font-size:1.25rem;opacity:.75;margin-top:.4rem}',

    '.pcart__also{margin:2.6rem 0 0;padding-top:2rem;border-top:.2rem solid rgb(62 71 40 / .14)}',
    '.pcart__also h3{font-family:var(--papi-font-display,sans-serif);font-weight:850;text-transform:uppercase;font-size:1.5rem;letter-spacing:.06em;margin:0 0 1rem;opacity:.75}',
    '.pcart-line__px{font-weight:700;font-size:1.5rem;white-space:nowrap}',
    '.pcart-line--shop{grid-template-columns:6.4rem 1fr auto}',
    '.pcart-line__meta{display:flex;align-items:center;gap:1.2rem;justify-content:flex-end}',
    '.pcart.is-busy{cursor:progress}',
    '.pcart.is-busy .pcart__body,.pcart.is-busy .pcart__foot{opacity:.55;pointer-events:none;transition:opacity .2s}',
    '.pcart__sub{display:flex;align-items:baseline;justify-content:space-between;font-size:1.4rem;opacity:.7;margin:0}',

    /* one card per carton */
    '.pcart-box{border:.2rem solid rgb(62 71 40 / .16);border-radius:1.8rem;padding:1.4rem;margin:0 0 1.4rem;background:rgb(255 255 255 / .3)}',
    '.pcart-box[data-active="1"]{border-color:var(--papi-green,#8e9839);background:rgb(255 255 255 / .55)}',
    '.pcart-box__top{display:flex;align-items:center;gap:1rem;margin:0 0 1rem}',
    '.pcart-box__nm{font-family:var(--papi-font-display,sans-serif);font-weight:850;text-transform:uppercase;font-size:1.7rem;line-height:1}',
    '.pcart-box__of{font-size:1.25rem;opacity:.65;margin-left:.2rem}',
    '.pcart-box__px{margin-left:auto;font-weight:800;font-size:1.6rem}',
    '.pcart-box__bin{width:2.8rem;height:2.8rem;border-radius:50%;display:grid;place-items:center;font-size:1.7rem;line-height:1;color:inherit;background:rgb(62 71 40 / .08);flex:none}',
    '.pcart-box__bin:hover{background:rgb(163 49 31 / .18)}',
    '.pcart-box__lines{list-style:none;margin:0;padding:0;display:grid;gap:.8rem}',
    '.pcart-box__empty{font-size:1.35rem;opacity:.6;margin:.2rem 0 0}',

    /* 3 / 6 switch, shown only where it cannot destroy a choice */
    '.pcart-size{display:inline-flex;border:.2rem solid rgb(62 71 40 / .28);border-radius:99rem;overflow:hidden;margin-top:1rem}',
    '.pcart-size button{padding:.5rem 1.2rem;font-family:var(--papi-font-display,sans-serif);font-weight:800;font-size:1.25rem;text-transform:uppercase;color:inherit;background:none}',
    '.pcart-size button[aria-pressed="true"]{background:var(--papi-green-dark,#3e4728);color:var(--papi-shell,#f9f9dd)}',
    '.pcart-size button:disabled{opacity:.3;cursor:not-allowed}',

    '.pcart__addbox{display:flex;gap:1rem;margin:0 0 2rem}',
    '.pcart__addbox button{flex:1;border:.2rem dashed rgb(62 71 40 / .3);border-radius:1.6rem;padding:1.2rem .8rem;font-family:var(--papi-font-display,sans-serif);font-weight:850;text-transform:uppercase;font-size:1.4rem;color:inherit;background:none;transition:border-color .25s,background-color .25s}',
    '.pcart__addbox button:hover{border-color:var(--papi-green-dark,#3e4728);background:rgb(255 255 255 / .4);border-style:solid}',
    '.pcart__addbox small{display:block;font-family:var(--papi-font-body,sans-serif);font-weight:500;font-size:1.15rem;opacity:.65;text-transform:none;margin-top:.2rem}',
    '.pcart__boxpic{display:block;height:8.2rem;margin:0 0 .8rem}',
    '.pcart__boxpic img{width:100%;height:100%;object-fit:contain;display:block}',
    '.pcart__boxnm{display:block;line-height:1.1}',
    '.pcart__addbox button:hover .pcart__boxpic img{transform:translateY(-.3rem)}',
    '.pcart__boxpic img{transition:transform .35s cubic-bezier(.16,1,.3,1)}',

    /* free shipping progress */
    '.pcart__ship{display:grid;gap:.7rem;margin:0 0 .4rem}',
    '.pcart__ship p{margin:0;font-size:1.35rem;line-height:1.4;font-weight:600}',
    '.pcart__ship b{font-weight:800}',
    '.pcart__shipbar{height:.7rem;border-radius:99rem;background:rgb(62 71 40 / .14);overflow:hidden}',
    '.pcart__shipbar i{display:block;height:100%;border-radius:99rem;background:var(--papi-green,#8e9839);'
      + 'transition:width .45s cubic-bezier(.16,1,.3,1)}',
    '.pcart__ship--on .pcart__shipbar i{background:var(--papi-green-dark,#3e4728)}',
    '.pcart__ship--on p{color:var(--papi-green-dark,#3e4728)}',
    /* gift with purchase */
    '.pcart__gwp{margin:.2rem 0 .6rem;font-size:1.3rem;line-height:1.45;font-weight:600}',
    '.pcart__gwp b{font-weight:800}',
    '.pcart__gwp a{color:inherit;text-decoration:underline;text-underline-offset:.25em}',
    '.pcart__gwp--on{color:var(--papi-green-dark,#3e4728)}',
    /* The lead time is the one thing in the bag a customer can be genuinely
       annoyed to discover late, so it gets a rule and its own space rather than
       being folded in with the shipping progress. */
    '.pcart__lead{margin:.2rem 0 .8rem;padding:.8rem 0 .8rem 1.1rem;'
      + 'border-left:.35rem solid var(--papi-green-dark,#3e4728);'
      + 'font-size:1.3rem;line-height:1.45;font-weight:600}',
    '.pcart__lead b{font-weight:850}'
  ].join('\n');

  /* ---------- DOM ------------------------------------------------------- */

  var veil, drawer, body, foot, toast, toastTimer;

  function boot() {
    paintBundleStock();               // grey out bundles whose carton has gone
    paintBarStock();                  // and the bars themselves, everywhere
    paintSeasonal();                  // seasonal bar cards that cannot be bought

    var style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    veil = document.createElement('div');
    veil.className = 'pcart-veil';
    veil.addEventListener('click', close);

    drawer = document.createElement('aside');
    drawer.className = 'pcart';
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('aria-label', 'Your bag');
    /* Lenis (homepage + the PDPs) calls preventDefault on wheel and touchmove
       at the window, which silently kills scrolling inside ANY nested
       scroller — so with a full box the drawer's content was unreachable below
       the fold. Calling lenis.stop() is not enough: it stops Lenis scrolling
       the page but the listener still cancels the event. `data-lenis-prevent`
       is the documented opt-out, and it covers wheel and touch. Set on the
       whole drawer so anything scrollable inside it keeps working. */
    drawer.setAttribute('data-lenis-prevent', '');
    drawer.innerHTML =
      '<div class="pcart__head">' +
        '<h2 data-pcart-title>Your bag</h2>' +
        '<button class="pcart__close" type="button" aria-label="Close bag">&times;</button>' +
      '</div>' +
      '<div class="pcart__body"></div>' +
      '<div class="pcart__foot"></div>';
    drawer.querySelector('.pcart__close').addEventListener('click', close);

    body = drawer.querySelector('.pcart__body');
    foot = drawer.querySelector('.pcart__foot');

    toast = document.createElement('div');
    toast.className = 'pcart-toast';
    toast.setAttribute('role', 'status');

    document.body.appendChild(veil);
    document.body.appendChild(drawer);
    document.body.appendChild(toast);

    /* delegated so buttons rendered later still work */
    document.addEventListener('click', function (e) {
      var add = e.target.closest('[data-add-bar]');
      if (add) { e.preventDefault(); addBar(add.getAttribute('data-add-bar'), parseInt(add.getAttribute('data-add-qty'), 10) || 1); return; }
      var av = e.target.closest('[data-add-variant]');
      if (av) {
        e.preventDefault();
        var qty = parseInt(av.getAttribute('data-add-qty'), 10) || 0;
        var from = av.getAttribute('data-qty-from');
        if (!qty && from) {
          var input = document.querySelector(from);
          qty = input ? parseInt(input.value, 10) : 0;
        }
        addVariant(av.getAttribute('data-add-variant'), qty > 0 ? qty : 1,
                   null, av.getAttribute('data-add-name'));
        return;
      }
      /* Delegated, so [data-addbox] works from the HOMEPAGE pack picker as well
         as from inside the drawer. It must be delegated ONLY — the drawer's
         render() used to bind these directly, and having both would start two
         boxes on one click. */
      var ab = e.target.closest('[data-addbox]');
      if (ab) { e.preventDefault(); addBox(parseInt(ab.getAttribute('data-addbox'), 10)); return; }
      var open = e.target.closest('[data-cart-open]');
      if (open) { e.preventDefault(); openDrawer(); }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('on')) close();
    });

    window.addEventListener('storage', function (e) {
      if (e.key === KEY) { boxes = read(); render(); }
    });

    render();
    pullCart().then(function () { reconcile(); return healCart(); }).then(render);
  }

  function flash(msg) {
    toast.textContent = msg;
    toast.classList.add('on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.classList.remove('on'); }, 2600);
  }

  function openDrawer() {
    render();
    if (V) pullCart().then(render);
    veil.classList.add('on');
    drawer.classList.add('on');
    drawer.setAttribute('aria-hidden', 'false');
    if (window.__papiLenis) window.__papiLenis.stop();
    else document.documentElement.style.overflow = 'hidden';
  }

  function close() {
    veil.classList.remove('on');
    drawer.classList.remove('on');
    drawer.setAttribute('aria-hidden', 'true');
    if (window.__papiLenis) window.__papiLenis.start();
    else document.documentElement.style.overflow = '';
  }

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ---------- render ---------------------------------------------------- */

  function boxCard(b, i, active) {
    var n = count(b);
    var pips = '';
    for (var p = 1; p <= b.size; p++) pips += '<i class="' + (p <= n ? 'f' : '') + '"></i>';

    var lines = FLAVOURS.filter(function (f) { return b.bars[f.slug]; }).map(function (f) {
      return '<li class="pcart-line">' +
        '<span class="pcart-line__sw"><img src="' + ASSETS + 'papi-bar-' + f.slug + '-pack-cut.png" alt="" loading="lazy"></span>' +
        '<span><span class="pcart-line__nm">' + f.name + '</span>' +
        '<span class="pcart-line__sub">Chunky bar</span></span>' +
        '<span class="pcart-step">' +
          '<button type="button" data-b="' + i + '" data-q="' + f.slug + '" data-d="-1" aria-label="One less ' + f.name + ' in box ' + (i + 1) + '">&minus;</button>' +
          '<span>' + b.bars[f.slug] + '</span>' +
          '<button type="button" data-b="' + i + '" data-q="' + f.slug + '" data-d="1" aria-label="One more ' + f.name + ' in box ' + (i + 1) + '"' +
            (n >= b.size ? ' disabled style="opacity:.3;cursor:not-allowed"' : '') + '>+</button>' +
        '</span></li>';
    }).join('');

    /* Only offer the switch when it is safe in at least one direction. */
    var sizeBtns = SIZES.map(function (sz) {
      var on = sz === b.size;
      var ok = on || canResize(b, sz);
      return '<button type="button" data-size="' + sz + '" data-b="' + i + '"' +
        ' aria-pressed="' + on + '"' + (ok ? '' : ' disabled') +
        ' aria-label="Make box ' + (i + 1) + ' a ' + sz + '-pack">' + sz + '-pack</button>';
    }).join('');

    return '<div class="pcart-box" data-active="' + (active ? 1 : 0) + '">' +
      '<div class="pcart-box__top">' +
        '<span class="pcart-box__nm">Box ' + (i + 1) +
          '<span class="pcart-box__of"> · ' + n + ' of ' + b.size + '</span></span>' +
        '<span class="pcart-box__px">' + money(PRICE[b.size]) + '</span>' +
        '<button class="pcart-box__bin" type="button" data-rm="' + i + '" aria-label="Remove box ' + (i + 1) + '">&times;</button>' +
      '</div>' +
      '<div class="pcart__meter" aria-hidden="true">' + pips + '</div>' +
      (lines ? '<ul class="pcart-box__lines">' + lines + '</ul>'
             : '<p class="pcart-box__empty">Empty — pick ' + b.size + ' bars below.</p>') +
      '<div class="pcart-size">' + sizeBtns + '</div>' +
    '</div>';
  }

  /* The offers that replace the box builder while the bars are out. Written
     as one block with the tee first: it is the bigger carrot ($58 of shirt for
     a $100 basket) and the threshold is lower, so it is the one most bags can
     actually reach. Free shipping sits under it as the second step.

     Both numbers come from the same helpers the footer bars use, so the drawer
     cannot tell a customer two different things about the same threshold. */
  function promoBlock() {
    var g = gwpStatus();
    var sh = shipProgress();
    var teeLine = g.hasTee
      ? '<b>Free tee added.</b> Papi approves.'
      : (g.unlocked
          ? '<b>You have earned a free tee.</b> <a href="' + GWP_LINK + '">Pick yours</a> — it will not add itself.'
          : '<b>' + money(g.remaining) + ' away</b> from a <a href="' + GWP_LINK + '">free tee</a>.');
    var shipLine = sh.unlocked
      ? '<b>' + (sh.express ? 'Free express shipping' : 'Free shipping') + ' unlocked.</b> Papi is paying.'
      : '<b>' + money(sh.remaining) + ' away</b> from ' +
        (sh.express ? 'free express shipping' : 'free shipping') + '.';

    return '<div class="pcart__promo">' +
      '<p class="pcart__promo-h">Chunky bars are between drops.</p>' +
      '<p class="pcart__promo-l">' + teeLine + '</p>' +
      '<p class="pcart__promo-l">' + shipLine + '</p>' +
      '</div>';
  }

  function render() {
    var s = status();
    drawer.setAttribute('data-tone', s.tone);
    drawer.classList.toggle('is-busy', busy);

    var heading = drawer.querySelector('[data-pcart-title]');
    if (heading) heading.textContent = s.boxes ? 'Your Chunky Boxes' : 'Your bag';

    var active = activeIndex();
    var cards = boxes.map(function (b, i) { return boxCard(b, i, i === active); }).join('');

    /* The cartons are a real product feature, not just a size choice, so the
       picker shows the actual box. `contain` because the 3-pack is portrait
       (0.82) and the 6-pack landscape (1.28) — a cover-crop would slice one of
       them and they would stop reading as the same family of thing. */
    var addBoxBtns = SIZES.map(function (sz) {
      var gone = !boxAvailable(sz);
      return '<button type="button" data-addbox="' + sz + '"' +
        (gone ? ' disabled aria-disabled="true"' : '') + '>' +
        '<span class="pcart__boxpic"><img src="' + ASSETS + 'papi-box-' + sz + '.webp"' +
          ' alt="Chunky ' + sz + '-bar gift box" loading="lazy"></span>' +
        '<span class="pcart__boxnm">' + (boxes.length ? 'Another ' + sz + '-pack' : sz + '-pack') + '</span>' +
        '<small>' + (gone ? 'Sold out' : sz + ' bars · ' + money(PRICE[sz])) + '</small></button>';
    }).join('');

    /* Bar chips fill the active box. With no box open the first click starts a
       3-pack, so the label says so rather than surprising anyone. */
    var chips = FLAVOURS.map(function (f) {
      return '<li><button class="pcart-chip" type="button" data-add-bar="' + f.slug + '"' +
        ' aria-label="Add a ' + f.name + ' bar' + (active === -1 ? ' and start a 3-pack' : ' to box ' + (active + 1)) + '">' +
        '<img src="' + ASSETS + 'papi-bar-' + f.slug + '-pack-cut.png" alt="" loading="lazy">' +
        '<span>' + f.name + '</span></button></li>';
    }).join('');

    var also = shopLines.map(function (l) {
      return '<li class="pcart-line pcart-line--shop">' +
        '<span class="pcart-line__sw">' + (l.image ? '<img src="' + esc(l.image) + '" alt="" loading="lazy">' : '') + '</span>' +
        '<span><span class="pcart-line__nm">' + esc(l.product_title) + '</span>' +
        '<span class="pcart-line__sub">' +
          (l.variant_title && l.variant_title !== 'Default Title' ? esc(l.variant_title) : 'Papi') +
        '</span></span>' +
        '<span class="pcart-line__meta">' +
          '<span class="pcart-line__px">' + money(l.line_price) + '</span>' +
          '<span class="pcart-step">' +
            '<button type="button" data-k="' + esc(l.key) + '" data-n="' + (l.quantity - 1) + '" aria-label="One less">&minus;</button>' +
            '<span>' + l.quantity + '</span>' +
            '<button type="button" data-k="' + esc(l.key) + '" data-n="' + (l.quantity + 1) + '" aria-label="One more">+</button>' +
          '</span>' +
        '</span></li>';
    }).join('');

    /* THE DRAWER HAS TWO DEFAULT STATES, and which one it is depends entirely
       on whether a bar can be bought today.

       BARS IN: the box builder owns the top of the drawer, because building a
       box is the highest-value thing anyone can do in here.

       BARS OUT: the builder is not merely disabled, it is GONE. Two dead
       "Sold out" carton buttons at the top of a bag are worse than nothing —
       they are the first thing a customer sees, they cannot be acted on, and
       they push the things that CAN be acted on below the fold. The slot goes
       to the two live offers instead: the free tee at $100 and free shipping.
       Those move real money and are reachable with what is actually in stock.

       The swap is cart-only. The tee messaging in the banner, on the PDPs, on
       the homepage and on Shop All stays put in both states. */
    var barsOut = !barsBuyable();
    var promo = barsOut ? promoBlock() : '';

    body.innerHTML =
      '<p class="pcart__msg">' + s.message + '</p>' +
      cards +
      (barsOut ? promo : '<div class="pcart__addbox">' + addBoxBtns + '</div>') +
      (boxes.length && !barsOut
        ? '<div class="pcart__more"><h3>' +
            (active === -1 ? 'Add a bar (starts a new box)' : 'Add to box ' + (active + 1)) +
          '</h3><ul class="pcart__chips">' + chips + '</ul></div>'
        : '') +
      (also ? '<div class="pcart__also"><h3>Also in your bag</h3>' +
              '<ul class="pcart__lines">' + also + '</ul></div>' : '');

    body.querySelectorAll('[data-q]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var i = parseInt(btn.getAttribute('data-b'), 10);
        var slug = btn.getAttribute('data-q');
        setQty(i, slug, (boxes[i].bars[slug] || 0) + parseInt(btn.getAttribute('data-d'), 10));
      });
    });
    body.querySelectorAll('[data-rm]').forEach(function (btn) {
      btn.addEventListener('click', function () { removeBox(parseInt(btn.getAttribute('data-rm'), 10)); });
    });
    body.querySelectorAll('[data-size]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setBoxSize(parseInt(btn.getAttribute('data-b'), 10), parseInt(btn.getAttribute('data-size'), 10));
      });
    });
    /* NOT bound here — [data-addbox] is handled by the delegated listener in
       boot(), so the homepage pack picker works too. Binding it in both places
       starts two boxes per click. */
    body.querySelectorAll('[data-k]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        changeLine(btn.getAttribute('data-k'), parseInt(btn.getAttribute('data-n'), 10));
      });
    });

    var otherCents = shopLines.reduce(function (n, l) { return n + l.line_price; }, 0);
    var canGo = (s.boxes === 0 ? shopLines.length > 0 : s.valid);
    var grand = (s.valid ? s.price : 0) + otherCents;

    /* Free shipping progress. Hidden on an empty bag — there is nothing to make
       progress against, and an empty "you're $65 away" bar is just nagging.
       The word "express" is carried only when the bag holds a bar, so the higher
       number reads as a product fact (chocolate ships chilled) rather than as a
       worse deal. */
    var ship = shipProgress();
    var shipWord = ship.express ? 'free express shipping' : 'free shipping';

    /* Stated here as well as on the PDPs, because the shop grid lets you add a
       bar without ever opening one — this is the last place before checkout
       where the wait can be set as an expectation rather than a surprise. */
    var leadHtml = bagHasChunky()
      ? '<p class="pcart__lead"><b>Made fresh to order.</b> Your Chunky bars are ' +
        'made once you order, so allow 1&ndash;2 weeks. They then ship express, chilled.</p>'
      : '';
    var shipHtml = (ship.spent > 0)
      ? '<div class="pcart__ship' + (ship.unlocked ? ' pcart__ship--on' : '') + '">' +
          '<p>' + (ship.unlocked
            ? '<b>' + (ship.express ? 'Free express shipping' : 'Free shipping') +
              ' unlocked.</b> Papi is paying.'
            : '<b>' + money(ship.remaining) + ' away</b> from ' + shipWord +
              (ship.express ? '. Chocolate ships chilled.' : '.')) + '</p>' +
          '<div class="pcart__shipbar" role="progressbar" aria-valuemin="0" aria-valuemax="100"' +
            ' aria-valuenow="' + ship.pct + '"' +
            ' aria-label="Progress towards ' + shipWord + '">' +
            '<i style="width:' + ship.pct + '%"></i></div>' +
        '</div>'
      : '';

    /* The gift line sits under the shipping bar as ONE line of text, not a
       second progress bar — two bars racing each other in a drawer reads as a
       dashboard, and the shipping one is the bigger promise. */
    var gwp = gwpStatus();
    var gwpHtml = '';
    if (gwp.hasTee) {
      gwpHtml = '<p class="pcart__gwp pcart__gwp--on"><b>Free tee added.</b> ' +
        (gwp.unlocked ? 'Papi approves.'
                      : money(gwp.remaining) + ' more and it is on the house.') + '</p>';
    } else if (gwp.unlocked) {
      gwpHtml = '<p class="pcart__gwp pcart__gwp--on"><b>You have earned a free tee.</b> ' +
        '<a href="' + GWP_LINK + '">Pick yours</a> \u2014 it will not add itself.</p>';
    } else if (gwp.spent > 0) {
      gwpHtml = '<p class="pcart__gwp">' + money(gwp.remaining) +
        ' more and a <a href="' + GWP_LINK + '">tee</a> is free too.</p>';
    }

    foot.innerHTML =
      leadHtml + shipHtml + gwpHtml +
      (s.boxes && otherCents
        ? '<p class="pcart__sub"><span>' + s.boxes + ' ' + (s.boxes === 1 ? 'box' : 'boxes') +
          '</span><span>' + money(s.price) + '</span></p>'
        : '') +
      '<div class="pcart__tot"><span>' + bagCount() + ' ' + (bagCount() === 1 ? 'item' : 'items') + '</span>' +
        '<span>' + (canGo ? money(grand) : '—') + '</span></div>' +
      '<button class="pcart__go" type="button"' + (canGo && !busy ? '' : ' disabled') + '>' +
        (busy ? 'One moment…' : (canGo ? 'Checkout · ' + money(grand) : 'Checkout')) + '</button>' +
      '<p class="pcart__note">3 bars $35 · 6 bars $65 · as many boxes as you like</p>';

    foot.querySelector('.pcart__go').addEventListener('click', checkout);


    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.innerHTML = '<span class="pcart-badge' + (s.valid ? ' ready' : '') + '"><b>' + bagCount() + '</b></span>';
    });
  }

  /* ---------- coming back from checkout ---------------------------------- */

  /* Did they pay, or did they press back? Both land here, and the only thing
     that tells them apart is whether our lines survive in the Shopify cart:
     a completed order empties it, backing out does not. */
  function reconcile() {
    var pending;
    try { pending = localStorage.getItem(PENDING_KEY); } catch (e) { pending = null; }
    if (!pending) return;
    try { localStorage.removeItem(PENDING_KEY); } catch (e) {}

    // Cart emptied => the order went through, so the builder has done its job.
    // Cart intact => they backed out, and their mix is exactly what they want
    // to see when the drawer opens again.
    if (!cartHasChunky) { boxes = []; write(); }
  }

  /* THE BACK BUTTON DOES NOT RE-RUN THE PAGE. Browsers restore it from the
     back/forward cache with every variable exactly as it was left — and
     checkout() deliberately leaves `busy` true, because it was navigating away
     and never expected to run again. Restored, that stale `true` renders the
     drawer disabled with "One moment…" on the button forever: the freeze.
     `pageshow` is the only event that fires on a bfcache restore; `load` and
     DOMContentLoaded do not. */
  window.addEventListener('pageshow', function (e) {
    busy = false;
    if (!V) { render(); return; }
    pullCart().then(function () { reconcile(); return healCart(); }).then(render);
  });

  /* ---------- go -------------------------------------------------------- */

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.PapiCart = {
    add: addBar, addVariant: addVariant, addLines: addLines,
    open: openDrawer, close: close,
    status: status, flavours: FLAVOURS, checkout: checkout,
    addBox: addBox, removeBox: removeBox, setBoxSize: setBoxSize,
    boxes: function () { return boxes; }, contents: contents,
    refresh: function () { return pullCart().then(render); },
    flash: flash
  };
})();

/* ---------------------------------------------------------------------------
   Klaviyo loader. papi-cart.js is the ONLY script every page loads (the shell
   pages also take papi-shell.js, the homepage papi-nutgame.js, the PDPs
   neither), so hanging the subscriber off it is what makes the footer form
   work on all of them without touching six generators. Same self-injecting
   pattern papi-cart.js already uses for its own drawer.
   --------------------------------------------------------------------------- */
(function () {
  /* CARRY THE ?v= ACROSS. This used to be a bare
   *   s.src.replace(/papi-cart\.js.*$/, '')
   * and the `.*$` ate papi-cart.js's own `?v=<hash>` along with the filename,
   * so the injected script was requested VERSIONLESS. Shopify serves theme
   * assets with `cache-control: max-age=31557600`, so the CDN pinned whatever
   * copy it first saw and never looked again: on 2026-09-01 the live site was
   * executing the 18 August build (age 14 days), which predates the
   * back-in-stock code entirely. `.bis__form` therefore had NO submit handler
   * and every sold-out-PDP waitlist address from 26 Aug onward was discarded
   * by a native form submit. The theme file was correct the whole time — only
   * the cache was stale, which is why reading the file off the theme said
   * everything was fine.
   *
   * Liquid's `asset_url` versions every asset referenced from HTML. Anything a
   * script builds at runtime is outside that, so it has to do this itself.
   * Inheriting papi-cart.js's own hash means the companion busts at exactly
   * the moment the theme deploys, and never otherwise. */
  var s = document.currentScript;
  var src = (s && s.src) || '';
  var m = src.match(/^(.*\/)papi-cart\.js(\?.*)?$/);
  /* The fallback chain matters: a missed match must still yield a WORKING
     absolute base, or the relative 'assets/' resolves against the page path
     (/products/<handle>/assets/…) and 404s. So only the version is lost if the
     filename ever gains a suffix this pattern does not anticipate. */
  var base = m ? m[1] : (src ? src.replace(/papi-cart\.js.*$/, '') : 'assets/');
  var ver = (m && m[2]) || '';

  function load(file, already) {
    if (already) return;
    var el = document.createElement('script');
    el.src = base + file + ver;
    el.defer = true;
    document.head.appendChild(el);
  }

  load('papi-klaviyo.js', window.papiSubscribe);
  /* papi-drop.js IS DELIBERATELY NOT LOADED. The drop date came off every
     surface on 2026-08-26 — customers are told to join the list rather than
     given a countdown, so nothing reads [data-drop-countdown] any more and the
     clock would be ~7.5 KB of dead weight on every page.

     The module is kept in the repo, tested and correct across the October DST
     switch. If a public countdown ever comes back, restore the load() call
     below and the markup; nothing else needs rebuilding.
         load('papi-drop.js', window.PapiDrop);   */
})();

/* ==========================================================================
   TRICK OR TREAT REFUND DRAW — top-banner countdown (Chris, 22 Oct 2026).

   One winning order is drawn at 8pm AEDT on 21, 22 and 23 Oct 2026, from
   orders containing at least one CHUNKY Spooky Edition bar (T&Cs in the
   REFUND-DRAW Klaviyo campaigns). Until the last draw the free-tee banner is
   replaced by a countdown to the next 8pm draw; after it, the original banner
   markup is put back untouched, so this block goes inert on its own and can
   be deleted at leisure.

   The draw times are absolute instants (8pm AEDT = 09:00 UTC), so the clock
   is right whatever time zone the shopper's device is set to. "Tonight" vs
   "tomorrow" is decided in Melbourne time for the same reason.

   It lives here because papi-cart.js is the one script on every page. The
   bar's own CSS (papi-shell.css) is nowrap with an ellipsis and hides
   .topbar__extra under 700px, so the entry rule sits in that slot and phones
   show the short line only; the headline itself links to the Spooky page. */
(function () {
  var DRAWS = ['2026-10-21T09:00:00Z', '2026-10-22T09:00:00Z', '2026-10-23T09:00:00Z']
    .map(function (s) { return new Date(s).getTime(); });
  var LINK = '/products/chunky-spooky-edition';
  if (Date.now() >= DRAWS[DRAWS.length - 1]) return;

  function melDay(t) {
    try {
      return new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Melbourne',
        year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(t));
    } catch (e) { return new Date(t + 11 * 3600e3).toISOString().slice(0, 10); }
  }
  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function start() {
    var bar = document.querySelector('.topbar p');
    if (!bar) return;
    var original = bar.innerHTML;
    var timer = null;
    /* The bar's CSS is copied inline into several page shells, so the few
       rules this needs travel with it instead of being edited into each copy. */
    var css = document.createElement('style');
    css.textContent = '.topbar__clock{font-variant-numeric:tabular-nums}' +
      '.topbar__short{display:none}' +
      '@media (max-width:700px){.topbar__short{display:inline}.topbar__long{display:none}}';
    document.head.appendChild(css);

    function nextDraw(now) {
      for (var i = 0; i < DRAWS.length; i++) if (DRAWS[i] > now) return DRAWS[i];
      return null;
    }
    function render() {
      var now = Date.now(), d = nextDraw(now);
      if (!d) { clearInterval(timer); bar.innerHTML = original; css.remove(); return; }
      var s = Math.max(0, Math.floor((d - now) / 1000));
      var clock = Math.floor(s / 3600) + 'h ' + pad(Math.floor(s % 3600 / 60)) + 'm ' + pad(s % 60) + 's';
      var when = melDay(d) === melDay(now) ? 'tonight’s' : 'tomorrow’s';
      /* Phones get "Draw in <clock>" — the full sentence is 451px against a
         343px bar at 375px wide, and this bar truncates rather than wraps. */
      bar.innerHTML = '<a href="' + LINK + '"><b>Win your order back.</b></a> ' +
        '<span class="topbar__short">Draw in </span>' +
        '<span class="topbar__clock">' + clock + '</span>' +
        '<span class="topbar__long"> till ' + when + ' 8pm draw</span>' +
        '<span class="topbar__sep" aria-hidden="true">&mdash;</span>' +
        '<span class="topbar__extra">add a Spooky Edition bar to enter</span>';
    }
    render();
    timer = setInterval(render, 1000);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
